import {useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {zodResolver} from "@hookform/resolvers/zod";
import {SubmitHandler, useForm} from "react-hook-form";
import {z} from "zod";
import {
    Form,
    FormControl, FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {Button} from "@/components/ui/button";
import {useToast} from "@/components/ui/use-toast";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {useMutation, useQuery} from "@tanstack/react-query";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {disciplineOptions, regionOptions, studentDegrees} from "@/utils/profile-info.ts";
import {getMenteeById, updateMenteeWithMetadata} from "@/services/menteeService.ts";
import {Mentee, UpdateMenteeMetaDto} from "@/types/Mentee.ts";
import {Loader2} from "lucide-react";
import {useTranslation} from "react-i18next";
import {activeLocale, LocalizedField} from "@/utils/translation-helper.ts";
import {fillTranslatedFields} from "@/services/translateService.ts";

const MenteeProfileEdit = () => {
    const {t} = useTranslation();
    const {t: vt} = useTranslation("validation");
    const {id} = useParams();
    const navigate = useNavigate();
    const {toast} = useToast();

    const {data: mentee} = useQuery({
        queryKey: queryKeys.fetchMentees.one,
        queryFn: () => getMenteeById(id ? parseInt(id) : -1),
    });

    const formSchema = z.object({
        institution: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentee.institution")}),
        }),
        institutionFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentee.institution")}),
        }),
        studentStatus: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentee.studentStatus")}),
        }),
        studentStatusFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentee.studentStatus")}),
        }),
        discipline: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("criteria.discipline")}),
        }),
        disciplineFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("criteria.discipline")}),
        }),
        supervisor: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentee.supervisor")}),
        }),
        region: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.region")}),
        }),
        regionFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.region")}),
        }),
        bio: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentee.bio")}),
        }),
        bioFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentee.bio")}),
        }),
        phoneNumber: z.string().regex(
            /^(\(\d{3}\)\s|\d{3}[-.\s])\d{3}[-.\s]\d{4}$/,
            {
                message: vt("phoneNumberWrongFormat"),
            }
        ),
    }).partial();

    type ProfileFormValues = z.infer<typeof formSchema>;

    const form = useForm<ProfileFormValues>({
        resolver: zodResolver(formSchema),
        mode: "onChange",
    });

    useEffect(() => {
        if (mentee) {
            if (activeLocale() === "fr") {
                form.reset({
                    institutionFr: mentee.institutionFr ?? "",
                    studentStatusFr: mentee.studentStatusFr ?? "",
                    disciplineFr: mentee.disciplineFr ?? "",
                    regionFr: mentee.regionFr ?? "",
                    bioFr: mentee.bioFr ?? "",
                    supervisor: mentee.supervisor ?? "",
                    phoneNumber: mentee.phoneNumber ?? "",
                });
            } else {
                form.reset({
                    institution: mentee.institution ?? "",
                    studentStatus: mentee.studentStatus ?? "",
                    discipline: mentee.discipline ?? "",
                    region: mentee.region ?? "",
                    bio: mentee.bio ?? "",
                    supervisor: mentee.supervisor ?? "",
                    phoneNumber: mentee.phoneNumber ?? "",
                });
            }
        }
    }, [mentee]);

    const onRequestError = () => {
        toast({
            variant: 'destructive',
            title: t("common.errorTitle"),
            description: t("common.errorDesc"),
        });
    };

    const onUpdateSuccess = async (mentee: Mentee) => {
        navigate(`/mentees/${mentee.id}`);
        toast({
            description: t("profile.updateSuccess"),
        });
    };

    const updateMutation = useMutation({
        mutationFn: ({id, ...dto}: Partial<UpdateMenteeMetaDto> & { id: number }) => updateMenteeWithMetadata(id, dto),
        onSuccess: onUpdateSuccess,
        onError: onRequestError,
    });

    const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (values: ProfileFormValues) => {
        if (!mentee) return;

        const dirtyFields = form.formState.dirtyFields as Partial<Record<keyof ProfileFormValues, boolean>>;
        const userTextPairsEnToFr = [
            { sourceKey: "institution" as const, targetKey: "institutionFr" as const },
            { sourceKey: "bio" as const, targetKey: "bioFr" as const },
        ];
        const userTextPairsFrToEn = [
            { sourceKey: "institutionFr" as const, targetKey: "institution" as const },
            { sourceKey: "bioFr" as const, targetKey: "bio" as const },
        ];

        try {
            if (activeLocale() === "fr") {
                const translated = await fillTranslatedFields({
                    pairs: userTextPairsFrToEn,
                    values: values as Record<string, string | undefined>,
                    dirtyFields,
                    existing: {
                        institution: mentee.institution ?? undefined,
                        bio: mentee.bio ?? undefined,
                    },
                    sourceLocale: "fr",
                    targetLocale: "en",
                });
                const updateDto = {
                    supervisor: values.supervisor,
                    phoneNumber: values.phoneNumber,
                    institutionFr: values.institutionFr,
                    studentStatusFr: values.studentStatusFr,
                    disciplineFr: values.disciplineFr,
                    bioFr: values.bioFr,
                    regionFr: values.regionFr,
                    studentStatus: studentDegrees.find(degree => degree.fr === values.studentStatusFr)?.en,
                    discipline: disciplineOptions.find(discipline => discipline.fr === values.disciplineFr)?.en,
                    region: regionOptions.find(region => region.fr === values.regionFr)?.en,
                    ...translated,
                };
                updateMutation.mutate({ ...updateDto, id: mentee.id });
            } else {
                const translated = await fillTranslatedFields({
                    pairs: userTextPairsEnToFr,
                    values: values as Record<string, string | undefined>,
                    dirtyFields,
                    existing: {
                        institutionFr: mentee.institutionFr ?? undefined,
                        bioFr: mentee.bioFr ?? undefined,
                    },
                    sourceLocale: "en",
                    targetLocale: "fr",
                });
                const updateDto = {
                    supervisor: values.supervisor,
                    phoneNumber: values.phoneNumber,
                    institution: values.institution,
                    studentStatus: values.studentStatus,
                    discipline: values.discipline,
                    bio: values.bio,
                    region: values.region,
                    studentStatusFr: studentDegrees.find(degree => degree.en === values.studentStatus)?.fr,
                    disciplineFr: disciplineOptions.find(discipline => discipline.en === values.discipline)?.fr,
                    regionFr: regionOptions.find(region => region.en === values.region)?.fr,
                    ...translated,
                };
                updateMutation.mutate({ ...updateDto, id: mentee.id });
            }
        } catch (err) {
            toast({
                variant: "destructive",
                title: t("common.errorTitle"),
                description: err instanceof Error ? err.message : t("common.errorDesc"),
            });
        }
    };

    return (
        <>
            <div className="container pb-12">
                <Card className="border border-border bg-bg-card shadow-sm">
                    <CardHeader className="border-b border-border">
                        <CardTitle className="text-xl font-semibold text-text-1">{t("profile.myProfile")}</CardTitle>
                        <CardDescription className="text-text-2">
                            {t("profile.myProfileDesc")}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                            <FormField
                                control={form.control}
                                name="supervisor"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t("mentee.supervisor")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("mentee.supervisorPlaceholder")} {...field} />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                name={activeLocale() === "fr" ? "institutionFr" : "institution"}
                                control={form.control}
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t("mentee.institution")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("mentee.institutionPlaceholder")} {...field} />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name={activeLocale() === "fr" ? "disciplineFr" : "discipline"}
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t("criteria.discipline")}</FormLabel>
                                        <Select onValueChange={field.onChange}
                                                value={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t("mentee.disciplinePlaceholder")}/>
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {disciplineOptions.map((discipline, idx) => (
                                                    <SelectItem key={idx} value={discipline[activeLocale() as keyof LocalizedField]}>
                                                        {discipline[activeLocale() as keyof LocalizedField]}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormDescription>

                                        </FormDescription>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name={activeLocale() === "fr" ? "studentStatusFr" : "studentStatus"}
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t("mentee.studentStatus")}</FormLabel>
                                        <Select onValueChange={field.onChange}
                                                value={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t("mentee.studentStatusPlaceholder")}/>
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {studentDegrees.map((studentStatus, idx) => (
                                                    <SelectItem key={idx} value={studentStatus[activeLocale() as keyof LocalizedField]}>
                                                        {studentStatus[activeLocale() as keyof LocalizedField]}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormDescription>

                                        </FormDescription>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name={activeLocale() === "fr" ? "regionFr" : "region"}
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t("mentor.region")}</FormLabel>
                                        <Select onValueChange={field.onChange}
                                                value={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t("mentee.regionPlaceholder")}/>
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent className="max-w-[360px] sm:max-w-fit overflow-auto">
                                                {regionOptions.map((region, idx) => (
                                                    <SelectItem key={idx} value={region[activeLocale() as keyof LocalizedField]}>
                                                        {region[activeLocale() as keyof LocalizedField]}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormDescription>

                                        </FormDescription>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name={activeLocale() === "fr" ? "bioFr" : "bio"}
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t("mentor.bio")}</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder={t("mentor.bioPlaceholder")}
                                                className="resize-none"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="phoneNumber"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>{t("common.phoneNumber")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("common.phoneNumberPlaceholder")} {...field} />
                                        </FormControl>
                                        <FormDescription>
                                            {t("common.phoneNumberFormat")}
                                        </FormDescription>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                            <Button
                                type="submit"
                                disabled={!form.formState.isDirty || updateMutation.isPending}
                            >
                                {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                                {t("profile.updateProfile")}
                            </Button>
                            </form>
                        </Form>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}

export default MenteeProfileEdit;
